/**
 * Module feature-flag catalog.
 *
 * Central home for the runtime feature flags that gate whole modules /
 * capabilities, and for keeping the `feature_flags` catalog rows in step with
 * them (the admin flips an override from Configuration > Capabilities).
 *
 * Who writes the catalog: only an identity the access gate admits as an
 * administrator (approved GlobalAdmin / OrgAdmin, the same predicate as
 * `public.is_app_admin()` in migration 00414, and the only identity that can
 * open Configuration > Capabilities). Everyone else reads the catalog and never
 * attempts the upsert. Before this gate the upsert ran from the App root effect
 * for every visitor, including the login wall with no session at all; that
 * anon request was refused by RLS (42501) and the browser logged it as a 401
 * console error on every load (issue #30).
 *
 * The entry point is `syncModuleFlags(access)`, called by <AccessGate> once the
 * signed-in identity is known. Future module switches are added to
 * MODULE_FLAGS here.
 */

import { describeFeatureFlagError, listFlags, registerFlag } from '@/services/featureFlagService'
import type { AccessRecord } from '@/services/accessControlService'

export interface ModuleFlagDef {
  key: string
  /** Friendly name shown on the Capabilities switch page. */
  label: string
  valueType: 'boolean'
  defaultValue: boolean
  category: string
  description: string
}

/** The master capability switches. Add future module toggles here. */
export const MODULE_FLAGS: ModuleFlagDef[] = [
  {
    key: 'agile.enabled',
    label: 'Agile Delivery',
    valueType: 'boolean',
    defaultValue: false,
    category: 'agile',
    description:
      'Master switch for the Agile Delivery capability (backlog, boards, sprints, agile insights, hybrid rollup).',
  },
  {
    key: 'startupChecklist.allProjectTypes',
    label: 'Startup Checklist on every project type',
    valueType: 'boolean',
    defaultValue: false,
    category: 'projects',
    description:
      'Off (default): the Startup Checklist appears only on construction / E&C projects that are in Startup status. On: it appears on any project type in Startup status.',
  },
]

/** Look up a module flag definition by key. */
export function getModuleFlag(key: string): ModuleFlagDef | undefined {
  return MODULE_FLAGS.find((f) => f.key === key)
}

/**
 * Access roles allowed to write the flag catalog. Mirrors `public.is_app_admin()`
 * (approved + access_role in GlobalAdmin / OrgAdmin), which is what gates the
 * admin surfaces that edit flags.
 */
export const FLAG_CATALOG_WRITER_ROLES: ReadonlyArray<string> = ['GlobalAdmin', 'OrgAdmin']

/** The slice of the access record the catalog needs to decide read vs write. */
export type FlagCatalogIdentity = Pick<AccessRecord, 'status' | 'accessRole'>

/** True only for an approved administrator; null / pending / suspended / any other role is a reader. */
export function canRegisterModuleFlags(access: FlagCatalogIdentity | null | undefined): boolean {
  if (!access) return false
  return access.status === 'approved' && FLAG_CATALOG_WRITER_ROLES.includes(access.accessRole)
}

export type ModuleFlagSyncResult =
  | { mode: 'registered' }
  | { mode: 'read'; missing: string[] }

/**
 * Administrator path: upsert every module flag definition (idempotent on key).
 * Each flag is attempted independently so one failure does not stop the rest,
 * but nothing is hidden: if any registration fails the promise rejects with
 * every failure named, and the caller decides how to report it.
 */
export async function registerModuleFlags(): Promise<void> {
  const results = await Promise.allSettled(
    MODULE_FLAGS.map((f) =>
      registerFlag({
        key: f.key,
        valueType: f.valueType,
        defaultValue: f.defaultValue,
        category: f.category,
        description: f.description,
        owner: 'platform',
      }),
    ),
  )
  const failed = results
    .map((r, i) => (r.status === 'rejected' ? `${MODULE_FLAGS[i]!.key}: ${describeFeatureFlagError(r.reason)}` : null))
    .filter((s): s is string => s !== null)
  if (failed.length > 0) {
    throw new Error(
      `could not register ${failed.length} of ${MODULE_FLAGS.length} module flags (${failed.join('; ')})`,
    )
  }
}

/**
 * Reader path: list the catalog and report which module flags have no row yet.
 * Never writes. A missing row is not an error: `useFlag` resolves an
 * unregistered key to undefined and the consumer falls back to the compiled
 * default, so the only consequence is that the switch is not visible on
 * Configuration > Capabilities until an administrator signs in.
 */
export async function readModuleFlags(): Promise<string[]> {
  const present = new Set((await listFlags()).map((f) => f.key))
  return MODULE_FLAGS.filter((f) => !present.has(f.key)).map((f) => f.key)
}

/**
 * Keep the catalog in step with MODULE_FLAGS for the signed-in identity:
 * an approved administrator registers, everyone else reads. Rejects when the
 * write or the read fails so the caller decides how to report it; nothing is
 * retried here.
 */
export async function syncModuleFlags(
  access: FlagCatalogIdentity | null | undefined,
): Promise<ModuleFlagSyncResult> {
  if (canRegisterModuleFlags(access)) {
    await registerModuleFlags()
    return { mode: 'registered' }
  }
  const missing = await readModuleFlags()
  if (missing.length > 0) {
    console.info(
      `[featureFlagCatalog] not registered yet: ${missing.join(', ')}. Compiled defaults apply until an administrator signs in.`,
    )
  }
  return { mode: 'read', missing }
}
